import {
  DEFAULT_IMAGE_FORMAT,
  IMAGE_FORMAT_TOOLS,
  IMAGE_FORMAT_TOOL_SETS,
} from "../../../../components/shared/imageToolbar/imageFormatModel";
import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  clampCropInsideImage,
  clampImageToCoverCrop,
  fitCropRectForAspectId,
  hostFramesToSourceRect,
  isFullCropRect,
  normalizeCropRect,
  normalizeHostCropFrame,
  normalizeImageFrame,
  sourceRectToHostCropFrame,
  cropImageObjectStyle,
} from "../../../../components/shared/imageToolbar/imageCropModel";
import {
  WATERMARK_CONTENT_TYPES,
  applyWatermarkPosition,
} from "../Popup/watermarkSettings";

export function isWatermarkLogoToolbar(settings = {}) {
  return (
    settings.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
    Boolean(settings.logoUrl)
  );
}

function resolveLogoCropRect(settings = {}) {
  if (settings.logoCropRect) {
    return normalizeCropRect(settings.logoCropRect);
  }
  const inset = Math.max(0, Math.min(40, Number(settings.logoCropInset) || 0));
  if (inset > 0) {
    const i = inset / 100;
    return normalizeCropRect({ x: i, y: i, w: 1 - 2 * i, h: 1 - 2 * i });
  }
  return null;
}

export function watermarkToImageFormat(settings = {}) {
  return {
    ...DEFAULT_IMAGE_FORMAT,
    size: Number(settings.size) || DEFAULT_IMAGE_FORMAT.size,
    tintColor: settings.logoTintColor || null,
    strokeColor: settings.logoStrokeColor || DEFAULT_IMAGE_FORMAT.strokeColor,
    strokeWidth: Number(settings.logoStrokeWidth) || 0,
    cornerRadius: Number(settings.logoCornerRadius) || 0,
    cropInset: Number(settings.logoCropInset) || 0,
    cropAspect: settings.logoCropAspect || "custom",
    cropRect: resolveLogoCropRect(settings),
    cropFrame: settings.logoCropFrame
      ? normalizeHostCropFrame(settings.logoCropFrame)
      : null,
    cropImageFrame: settings.logoCropImageFrame
      ? normalizeImageFrame(settings.logoCropImageFrame)
      : null,
    cropActive: Boolean(settings.logoCropActive),
    cropSessionStartRect: settings.logoCropSessionStartRect
      ? normalizeCropRect(settings.logoCropSessionStartRect)
      : null,
    cropSessionStartImageFrame: settings.logoCropSessionStartImageFrame
      ? normalizeImageFrame(settings.logoCropSessionStartImageFrame)
      : null,
    naturalWidth: Number(settings.logoNaturalWidth) || 0,
    naturalHeight: Number(settings.logoNaturalHeight) || 0,
    flipH: Boolean(settings.logoFlipH),
    flipV: Boolean(settings.logoFlipV),
    effect: settings.effect || DEFAULT_IMAGE_FORMAT.effect,
    opacity: Number(settings.opacity ?? DEFAULT_IMAGE_FORMAT.opacity),
    position: settings.position || DEFAULT_IMAGE_FORMAT.position,
    offsetX: Number(settings.offsetX) || 0,
    offsetY: Number(settings.offsetY) || 0,
    rotation: Number(settings.rotation) || 0,
    spacing: Number(settings.spacing ?? DEFAULT_IMAGE_FORMAT.spacing),
    margin: Number(settings.margin ?? DEFAULT_IMAGE_FORMAT.margin),
    tile: settings.tile || "single",
    lockAspectRatio: settings.lockAspectRatio !== false,
  };
}

export function imageFormatPatchToWatermark(patch, settings = {}) {
  const next = { ...settings };
  if (patch.size !== undefined) next.size = patch.size;
  if (patch.tintColor !== undefined) next.logoTintColor = patch.tintColor;
  if (patch.strokeColor !== undefined) next.logoStrokeColor = patch.strokeColor;
  if (patch.strokeWidth !== undefined) {
    next.logoStrokeWidth = Math.max(
      0,
      Math.min(40, Number(patch.strokeWidth) || 0)
    );
  }
  if (patch.cornerRadius !== undefined) {
    next.logoCornerRadius = Math.max(
      0,
      Math.min(50, Number(patch.cornerRadius) || 0)
    );
  }
  if (patch.cropAspect !== undefined) next.logoCropAspect = patch.cropAspect;
  if (patch.cropFrame !== undefined) {
    next.logoCropFrame =
      patch.cropFrame == null ? null : normalizeHostCropFrame(patch.cropFrame);
  }
  if (patch.cropImageFrame !== undefined) {
    next.logoCropImageFrame =
      patch.cropImageFrame == null
        ? null
        : normalizeImageFrame(patch.cropImageFrame);
  }
  if (patch.cropSessionStartImageFrame !== undefined) {
    next.logoCropSessionStartImageFrame =
      patch.cropSessionStartImageFrame == null
        ? null
        : normalizeImageFrame(patch.cropSessionStartImageFrame);
  }
  if (patch.cropRect !== undefined) {
    next.logoCropRect =
      patch.cropRect == null ? null : normalizeCropRect(patch.cropRect);
    next.logoCropInset = 0;
  }
  if (patch.cropActive !== undefined) {
    next.logoCropActive = Boolean(patch.cropActive);
    if (!patch.cropActive) {
      next.logoCropFrame = null;
      next.logoCropImageFrame = null;
      next.logoCropSessionStartImageFrame = null;
    }
  }
  if (patch.cropSessionStartRect !== undefined) {
    next.logoCropSessionStartRect =
      patch.cropSessionStartRect == null
        ? null
        : normalizeCropRect(patch.cropSessionStartRect);
  }

  const nw = Number(next.logoNaturalWidth) || 0;
  const nh = Number(next.logoNaturalHeight) || 0;

  if (patch.cropActive === true && !settings.logoCropActive) {
    // Logo fills the host; crop window nests inside it (never separated).
    const imageFrame = { ...DEFAULT_IMAGE_FRAME };
    const current = resolveLogoCropRect(settings) || { ...DEFAULT_CROP_RECT };
    const aspectId = patch.cropAspect ?? settings.logoCropAspect ?? "custom";
    const sourceRect =
      patch.cropRect != null
        ? normalizeCropRect(patch.cropRect)
        : fitCropRectForAspectId(aspectId, nw, nh, current);
    const cropFrame = clampCropInsideImage(
      sourceRectToHostCropFrame(sourceRect, imageFrame),
      imageFrame
    );
    next.logoCropImageFrame = imageFrame;
    next.logoCropFrame = cropFrame;
    next.logoCropRect = hostFramesToSourceRect(cropFrame, imageFrame);
    next.logoCropSessionStartRect =
      patch.cropSessionStartRect != null
        ? normalizeCropRect(patch.cropSessionStartRect)
        : current;
    next.logoCropSessionStartImageFrame = { ...DEFAULT_IMAGE_FRAME };
  }

  if (
    patch.cropAspect !== undefined &&
    (next.logoCropActive || patch.cropActive === true)
  ) {
    const imageFrame = normalizeImageFrame(
      next.logoCropImageFrame ?? DEFAULT_IMAGE_FRAME
    );
    const fromSource = hostFramesToSourceRect(
      next.logoCropFrame ??
        sourceRectToHostCropFrame(
          resolveLogoCropRect(next) || DEFAULT_CROP_RECT,
          imageFrame
        ),
      imageFrame
    );
    const fittedSource = fitCropRectForAspectId(
      patch.cropAspect,
      nw,
      nh,
      fromSource
    );
    const cropFrame = clampCropInsideImage(
      sourceRectToHostCropFrame(fittedSource, imageFrame),
      imageFrame
    );
    next.logoCropFrame = cropFrame;
    next.logoCropImageFrame = clampImageToCoverCrop(imageFrame, cropFrame);
    next.logoCropRect = hostFramesToSourceRect(
      cropFrame,
      next.logoCropImageFrame
    );
  }

  if (patch.cropFrame !== undefined && next.logoCropActive) {
    const imageFrame = normalizeImageFrame(
      next.logoCropImageFrame ?? DEFAULT_IMAGE_FRAME
    );
    if (next.logoCropFrame) {
      const cropFrame = clampCropInsideImage(next.logoCropFrame, imageFrame);
      const img = clampImageToCoverCrop(imageFrame, cropFrame);
      next.logoCropFrame = cropFrame;
      next.logoCropImageFrame = img;
      next.logoCropRect = hostFramesToSourceRect(cropFrame, img);
    }
  }

  if (patch.cropImageFrame !== undefined && next.logoCropActive) {
    const cropFrame = normalizeHostCropFrame(
      next.logoCropFrame ??
        sourceRectToHostCropFrame(
          resolveLogoCropRect(next) || DEFAULT_CROP_RECT,
          DEFAULT_IMAGE_FRAME
        )
    );
    const imageFrame = clampImageToCoverCrop(
      normalizeImageFrame(next.logoCropImageFrame),
      cropFrame
    );
    const nestedCrop = clampCropInsideImage(cropFrame, imageFrame);
    next.logoCropImageFrame = imageFrame;
    next.logoCropFrame = nestedCrop;
    next.logoCropRect = hostFramesToSourceRect(nestedCrop, imageFrame);
  }
  if (patch.cropInset !== undefined && patch.cropRect === undefined) {
    next.logoCropInset = Math.max(
      0,
      Math.min(40, Number(patch.cropInset) || 0)
    );
  }
  if (patch.flipH !== undefined) next.logoFlipH = Boolean(patch.flipH);
  if (patch.flipV !== undefined) next.logoFlipV = Boolean(patch.flipV);
  if (patch.effect !== undefined) {
    next.effect = ["none", "shadow", "outline", "glow"].includes(patch.effect)
      ? patch.effect
      : "none";
  }
  if (patch.opacity !== undefined) next.opacity = patch.opacity;
  if (patch.position !== undefined && patch.position !== settings.position) {
    let placed = applyWatermarkPosition(
      { ...next, position: patch.position },
      patch.position
    );
    if (patch.margin !== undefined) {
      placed = {
        ...placed,
        margin: Math.max(0, Math.min(0.4, Number(patch.margin) || 0)),
      };
    }
    if (patch.offsetX !== undefined) placed = { ...placed, offsetX: patch.offsetX };
    if (patch.offsetY !== undefined) placed = { ...placed, offsetY: patch.offsetY };
    return placed;
  }
  if (patch.margin !== undefined) {
    next.margin = Math.max(0, Math.min(0.4, Number(patch.margin) || 0));
    if (patch.offsetX === undefined) next.offsetX = 0;
    if (patch.offsetY === undefined) next.offsetY = 0;
  }
  if (patch.offsetX !== undefined) next.offsetX = patch.offsetX;
  if (patch.offsetY !== undefined) next.offsetY = patch.offsetY;
  if (patch.rotation !== undefined) {
    next.rotation = Math.max(-180, Math.min(180, Number(patch.rotation) || 0));
  }
  if (patch.spacing !== undefined) {
    next.spacing = Math.max(0, Math.min(3.96, Number(patch.spacing) || 0));
  }
  if (patch.lockAspectRatio !== undefined) {
    next.lockAspectRatio = Boolean(patch.lockAspectRatio);
  }
  return next;
}

/** Normalized crop rect for rendering (null = full image). */
export function getWatermarkLogoCropRect(settings = {}) {
  return resolveLogoCropRect(settings);
}

/** Source pixel rect inside the logo bitmap for canvas drawImage. */
export function watermarkLogoCropSourcePixels(
  cropRect,
  naturalWidth = 0,
  naturalHeight = 0
) {
  const nw = Math.max(1, Number(naturalWidth) || 1);
  const nh = Math.max(1, Number(naturalHeight) || 1);
  const r = normalizeCropRect(cropRect || DEFAULT_CROP_RECT);
  return {
    sx: r.x * nw,
    sy: r.y * nh,
    sw: Math.max(1, r.w * nw),
    sh: Math.max(1, r.h * nh),
  };
}

/** Start / retarget a crop session for the chosen aspect. */
export function beginWatermarkLogoCrop(settings = {}, aspectId = "custom") {
  const nw = Number(settings.logoNaturalWidth) || 0;
  const nh = Number(settings.logoNaturalHeight) || 0;
  const current = resolveLogoCropRect(settings) || { ...DEFAULT_CROP_RECT };
  const imageFrame = { ...DEFAULT_IMAGE_FRAME };
  const cropFrame = clampCropInsideImage(
    fitCropRectForAspectId(aspectId, nw, nh, current),
    imageFrame
  );
  return {
    ...settings,
    logoCropAspect: aspectId,
    logoCropFrame: cropFrame,
    logoCropImageFrame: imageFrame,
    logoCropRect: hostFramesToSourceRect(cropFrame, imageFrame),
    logoCropActive: true,
    logoCropSessionStartRect: current,
    logoCropSessionStartImageFrame: { ...DEFAULT_IMAGE_FRAME },
    logoCropInset: 0,
  };
}

export function resolveWatermarkCropHostFrames(settings = {}) {
  const imageFrame = normalizeImageFrame(
    settings.logoCropImageFrame ?? DEFAULT_IMAGE_FRAME
  );
  const rawCrop = settings.logoCropFrame
    ? normalizeHostCropFrame(settings.logoCropFrame)
    : sourceRectToHostCropFrame(
        resolveLogoCropRect(settings) || DEFAULT_CROP_RECT,
        imageFrame
      );
  const cropFrame = clampCropInsideImage(rawCrop, imageFrame);
  const coveredImage = clampImageToCoverCrop(imageFrame, cropFrame);
  return {
    cropFrame: clampCropInsideImage(cropFrame, coveredImage),
    imageFrame: coveredImage,
  };
}

export function getWatermarkImageFormatTools() {
  return [
    IMAGE_FORMAT_TOOLS.BG_REMOVER,
    ...IMAGE_FORMAT_TOOL_SETS.default,
  ];
}

/**
 * CSS bits for logo marks (preview + canvas overlay).
 * Caller still supplies translate/rotate base transform.
 */
export function watermarkLogoMarkStyle(settings = {}, baseTransform = "") {
  const strokeW = Math.max(0, Number(settings.logoStrokeWidth) || 0);
  const strokeColor = settings.logoStrokeColor || "#FFFFFF";
  const radius = Math.max(
    0,
    Math.min(50, Number(settings.logoCornerRadius) || 0)
  );
  const flipH = Boolean(settings.logoFlipH);
  const flipV = Boolean(settings.logoFlipV);
  const tint = settings.logoTintColor || null;
  const cropActive = Boolean(settings.logoCropActive);
  const cropRect = cropActive ? null : resolveLogoCropRect(settings);

  const flips = [];
  if (flipH) flips.push("scaleX(-1)");
  if (flipV) flips.push("scaleY(-1)");
  const transform = [baseTransform, ...flips].filter(Boolean).join(" ");

  const effect = settings.effect || "none";
  const effectColor = settings.logoTintColor || strokeColor || "#FFFFFF";

  const style = {
    transform,
    borderRadius: radius > 0 ? `${radius}px` : undefined,
    overflow: effect !== "none" ? "visible" : "hidden",
  };

  if (strokeW > 0) {
    style.boxShadow = `0 0 0 ${strokeW}px ${strokeColor}`;
  }

  // Effects + tint are applied on the <img> via SVG filters (WatermarkLogoMarkMedia).
  // Never put CSS filter on this wrapper — it breaks CSS mask-based tints in Chromium.
  const fullImageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  };

  return {
    style,
    tint,
    effect,
    effectColor,
    cropRect,
    cropActive,
    imgStyle: cropActive
      ? fullImageStyle
      : cropImageObjectStyle(cropRect || DEFAULT_CROP_RECT),
    hasCrop: Boolean(cropRect && !isFullCropRect(cropRect)),
  };
}

/**
 * Recolor logo by masking the tint to the image alpha (not a solid box).
 */
export function watermarkLogoTintStyle(tint, logoUrl) {
  if (!tint || !logoUrl) return null;
  const mask = `url("${String(logoUrl).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}")`;
  return {
    backgroundColor: tint,
    WebkitMaskImage: mask,
    maskImage: mask,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  };
}
